import { Dialog } from '@angular/cdk/dialog';
import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import {
  WarehouseCellResponse,
  WarehouseResponse,
} from '@nebalkon-workspace/storage-api';
import {
  NotButton,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { printStaffCellQrBatch } from '../utils/staff-entity-qr-print.util';
import { warehouseRouteSegment } from '../utils/warehouse-route.util';
import {
  buildWarehouseCellGridRows,
  toggleCellSelection,
} from './staff-warehouse-cell-grid.util';

@Component({
  selector: 'lib-staff-warehouse-cell-grid',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NotButton],
  templateUrl: './staff-warehouse-cell-grid.component.html',
  styleUrl: './staff-warehouse-cell-grid.component.scss',
})
export class StaffWarehouseCellGridComponent {
  readonly warehouse = input.required<WarehouseResponse>();
  readonly cells = input.required<WarehouseCellResponse[]>();
  readonly occupiedCellIds = input.required<ReadonlySet<string>>();
  readonly loading = input(false);
  readonly error = input<string | null>(null);

  readonly #router = inject(Router);
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #printer = inject(NiimbotPrinterService);
  readonly #dialog = inject(Dialog);

  protected readonly selectionMode = signal(false);
  protected readonly selectedCellIds = signal<ReadonlySet<string>>(new Set());
  protected readonly printingQr = signal(false);

  protected readonly gridRows = computed(() =>
    buildWarehouseCellGridRows(
      this.warehouse(),
      this.cells(),
      this.occupiedCellIds(),
    ),
  );

  protected readonly selectedCount = computed(() => this.selectedCellIds().size);

  protected readonly canPrintSelected = computed(
    () => this.selectionMode() && this.selectedCount() > 0 && !this.printingQr(),
  );

  protected onToggleSelectionMode(): void {
    const next = !this.selectionMode();
    this.selectionMode.set(next);
    if (!next) {
      this.selectedCellIds.set(new Set());
    }
  }

  protected isCellSelected(cellId: string | undefined): boolean {
    if (!cellId) {
      return false;
    }
    return this.selectedCellIds().has(cellId);
  }

  protected onSlotClick(cell: WarehouseCellResponse | null): void {
    if (!cell) {
      return;
    }

    if (this.selectionMode()) {
      this.selectedCellIds.update((current) =>
        toggleCellSelection(current, cell.id),
      );
      return;
    }

    const segment = warehouseRouteSegment(this.warehouse());
    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'warehouses',
      segment,
      'cells',
      cell.id,
    ]);
  }

  protected async onPrintSelected(): Promise<void> {
    const ids = [...this.selectedCellIds()];
    if (ids.length === 0 || this.printingQr()) {
      return;
    }

    this.printingQr.set(true);
    try {
      await printStaffCellQrBatch({
        entityIds: ids,
        printer: this.#printer,
        translate: this.#translate,
        notification: this.#notification,
        dialog: this.#dialog,
      });
    } finally {
      this.printingQr.set(false);
    }
  }

  protected slotAriaLabel(
    label: string,
    isOccupied: boolean,
    cellId: string | undefined,
  ): string {
    const statusKey = isOccupied
      ? 'admin.warehouses.cells.legendOccupied'
      : 'admin.warehouses.cells.legendAvailable';
    const status = this.#translate.instant(statusKey);
    const selected =
      cellId && this.isCellSelected(cellId)
        ? this.#translate.instant('admin.warehouses.cells.selected')
        : '';
    return [label, status, selected].filter(Boolean).join(', ');
  }
}
